import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FileCheck, ChevronDown, Target, Zap, Users, Globe, Loader2, Shield, ListChecks, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { WaitingUX } from "../components/WaitingUX";
import { EngineSelect } from "../components/FlowSelectors";
import { ENGINE_KEYS_LITE } from "../lib/models";

const API = process.env.REACT_APP_BACKEND_URL;

const PREFS_KEY = "resumeai_defaults";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); } catch { return {}; } };
const savePrefs = (p) => { const e = loadPrefs(); localStorage.setItem(PREFS_KEY, JSON.stringify({ ...e, ...p })); };

export default function ATSScorePage() {
  const navigate = useNavigate();
  const [scanId, setScanId] = useState("");
  const [aiModel, setAiModel] = useState(() => {
    const m = loadPrefs().aiModel;
    return ENGINE_KEYS_LITE.includes(m) ? m : "glm-5.2";
  });
  const [savedResumes, setSavedResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("technical");
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/resumes`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setSavedResumes(data);
        const def = data.find(r => r.is_default);
        if (def) setSelectedResumeId(def.id);
      })
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleCheck = useCallback(async () => {
    if (!selectedResumeId) { setError("Please select a resume."); return; }
    if (!jobDescription.trim()) { setError("Please paste a job description."); return; }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("saved_resume_id", selectedResumeId);
      fd.append("job_description", jobDescription);
      fd.append("ai_model", aiModel);
      fd.append("consume_credit", "1");
      const res = await axios.post(`${API}/api/scan`, fd);
      const sid = res.data.scan_id;
      setScanId(sid);
      const started = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/api/scan/status/${sid}`);
          if (s.data.status === "completed") {
            clearInterval(pollRef.current);
            setResult(s.data.result || {});
            setActiveTab("technical");
            setLoading(false);
          } else if (s.data.status === "failed") {
            clearInterval(pollRef.current);
            setError(s.data.error || "Analysis failed. Please try again.");
            setLoading(false);
          } else if (Date.now() - started > 240000) {
            clearInterval(pollRef.current);
            setError("Analysis timed out. Please try again.");
            setLoading(false);
          }
        } catch {
          clearInterval(pollRef.current);
          setError("Connection lost while analyzing. Please try again.");
          setLoading(false);
        }
      }, 1500);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Analysis failed");
      setLoading(false);
    }
  }, [selectedResumeId, jobDescription, aiModel]);

  const kb = result?.keyword_breakdown || null;
  const pct = kb?.match_score || 0;
  const circumference = 2 * Math.PI * 58;
  const strokeDash = (pct / 100) * circumference;
  const ringColor = pct >= 80 ? "#10B981" : pct >= 50 ? "#F59E0B" : "#EF4444";

  const groups = kb?.groups || {};
  const groupOf = (name) => Array.isArray(groups[name]) ? groups[name] : [];
  const groupPct = (name) => {
    const g = groupOf(name);
    const max = g.reduce((a, s) => a + (s.max || 0), 0);
    const earned = g.reduce((a, s) => a + (s.earned || 0), 0);
    return max ? Math.round((earned / max) * 100) : 0;
  };
  const allSkills = [...groupOf("technical"), ...groupOf("soft"), ...groupOf("domain")];
  const counts = {
    matched: allSkills.filter(s => s.state === "matched").length,
    partial: allSkills.filter(s => s.state === "partial").length,
    missing: allSkills.filter(s => s.state === "missing").length,
  };

  const improvements = [];
  allSkills.filter(s => s.state === "missing" && s.importance === "required")
    .slice(0, 6)
    .forEach(s => improvements.push({ text: `Add "${s.skill}" — required keyword (mentioned ${s.jd_count || 1}× in the JD)`, tone: "r" }));
  allSkills.filter(s => s.state === "partial").slice(0, 4)
    .forEach(s => improvements.push({ text: `Strengthen "${s.skill}" — it's mentioned but lacks depth or context`, tone: "y" }));
  (result?.recommendations || []).slice(0, 3)
    .forEach(r => improvements.push({ text: typeof r === "string" ? r : r.action, tone: "b" }));
  (result?.structural_warnings || []).slice(0, 3)
    .forEach(w => improvements.push({ text: w, tone: "y" }));

  return (
    <div className="ma-page" data-testid="ats-score-page">
      <div className="ma-hdr">
        <FileCheck size={26} strokeWidth={1.5} />
        <div>
          <h1 className="ma-h1">Match Score</h1>
          <p className="ma-sub">Same scoring engine as the optimizer — see exactly where you stand before you optimize</p>
        </div>
      </div>

      <div className="ma-card">
        <div className="ma-field">
          <label className="ma-lbl">Resume</label>
          <div className="ma-sel-wrap">
            <select data-testid="ats-resume-select" className="ma-sel" value={selectedResumeId} onChange={e => setSelectedResumeId(e.target.value)}>
              <option value="">Select a saved resume...</option>
              {savedResumes.map(r => (
                <option key={r.id} value={r.id}>{r.display_name || r.filename} {r.is_default ? "(Default)" : ""}</option>
              ))}
            </select>
            <ChevronDown size={16} className="ma-sel-icon" />
          </div>
        </div>
        <div className="ma-field">
          <label className="ma-lbl">Job Description</label>
          <textarea data-testid="ats-jd-input" className="ma-ta" rows={8} placeholder="Paste the full job description here..." value={jobDescription} onChange={e => setJobDescription(e.target.value)} />
        </div>
        <div className="ma-field">
          <label className="ma-lbl">AI Engine</label>
          <EngineSelect value={aiModel} onChange={(m) => { setAiModel(m); savePrefs({ aiModel: m }); }} keys={ENGINE_KEYS_LITE} testid="ats-engine-select" className="max-w-xs" />
        </div>
        {error && <p className="ma-err" data-testid="ats-error">{error}</p>}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <button data-testid="ats-check-btn" className="ma-btn-primary" onClick={handleCheck} disabled={loading}>
            {loading ? <><Loader2 size={18} className="ma-spin" /> Deep scanning (30–60s)...</> : <><Target size={18} /> Check Match Score</>}
          </button>
          <span className="ma-credit-note" data-testid="ats-credit-note">Uses 1 scan credit per check</span>
        </div>
      </div>

      {loading && !result && (
        <div className="ma-card" data-testid="ats-loading" style={{ paddingTop: 22, paddingBottom: 22 }}>
          <WaitingUX label="Deep scanning your resume…" />
        </div>
      )}

      {result && kb && (
        <div className="ma-results" data-testid="ats-results">
          {/* Hero: Score + Stats */}
          <div className="ma-card ma-hero-card">
            <div className="ma-hero">
              <div className="ma-ring-wrap" data-testid="match-percentage-ring">
                <svg viewBox="0 0 128 128" className="ma-ring-svg">
                  <circle cx="64" cy="64" r="58" fill="none" stroke="var(--border-main)" strokeWidth="7" opacity="0.3" />
                  <circle cx="64" cy="64" r="58" fill="none" stroke={ringColor} strokeWidth="7"
                    strokeDasharray={`${strokeDash} ${circumference}`} strokeLinecap="round" transform="rotate(-90 64 64)" className="ma-ring-anim" />
                </svg>
                <div className="ma-ring-center">
                  <span className="ma-ring-num" style={{ color: ringColor }} data-testid="match-percentage-value">{pct}</span>
                  <span className="ma-ring-pct">%</span>
                </div>
              </div>
              <div className="ma-hero-info">
                <div className={`ma-rec-badge ${pct >= 80 ? "ma-rec-g" : pct >= 50 ? "ma-rec-y" : "ma-rec-r"}`} data-testid="ats-recommendation">
                  <Shield size={18} />
                  <span>{pct >= 80 ? "Strong Match" : pct >= 50 ? "Moderate Match" : "Weak Match"}</span>
                </div>
                <div className="ma-sub-scores">
                  <SubScore label="Technical" value={groupPct("technical")} icon={<Zap size={14}/>} />
                  <SubScore label="Soft Skills" value={groupPct("soft")} icon={<Users size={14}/>} />
                  <SubScore label="Required Coverage" value={kb.required_coverage || 0} icon={<Shield size={14}/>} />
                </div>
                <div className="ma-stats-row" data-testid="match-stats">
                  <StatPill label="Matched" value={counts.matched} color="#10B981" />
                  <StatPill label="Partial" value={counts.partial} color="#F59E0B" />
                  <StatPill label="Missing" value={counts.missing} color="#EF4444" />
                </div>
              </div>
            </div>
          </div>

          <button data-testid="ats-optimize-btn"
            onClick={() => navigate("/app/optimize", { state: { fromMatchScore: true, scanId, scanResult: result, resumeId: selectedResumeId, jobDescription } })}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", padding: "15px 20px", borderRadius: 12, border: "none", cursor: "pointer", background: "var(--primary)", color: "#fff", fontSize: "0.95rem", fontWeight: 700, boxShadow: "0 10px 26px -10px var(--primary)" }}>
            <Sparkles size={18} /> Optimize this resume — reuse this analysis <ArrowRight size={16} />
          </button>

          {/* What to improve */}
          {improvements.length > 0 && (
            <div className="ma-card" data-testid="ats-improvements">
              <h3 className="ma-imp-title"><ListChecks size={17} /> What to improve</h3>
              <ul className="ma-imp-list" data-testid="improvements-list">
                {improvements.slice(0, 10).map((im, i) => (
                  <li key={i} className={`ma-imp-item ma-imp-${im.tone}`} data-testid={`improvement-${i}`}>{im.text}</li>
                ))}
              </ul>
              <p className="ma-imp-hint">Run the optimizer to lift these automatically — it uses this exact scoring.</p>
            </div>
          )}

          {/* Skill Breakdown Tabs */}
          <div className="ma-card" data-testid="skill-breakdown">
            <div className="ma-tabs">
              <button className={`ma-tab ${activeTab === "technical" ? "ma-tab-active" : ""}`} onClick={() => setActiveTab("technical")} data-testid="tab-technical">
                <Zap size={15} /> Technical ({groupOf("technical").length})
              </button>
              <button className={`ma-tab ${activeTab === "soft" ? "ma-tab-active" : ""}`} onClick={() => setActiveTab("soft")} data-testid="tab-soft">
                <Users size={15} /> Soft Skills ({groupOf("soft").length})
              </button>
              <button className={`ma-tab ${activeTab === "domain" ? "ma-tab-active" : ""}`} onClick={() => setActiveTab("domain")} data-testid="tab-domain">
                <Globe size={15} /> Domain ({groupOf("domain").length})
              </button>
            </div>
            <div className="ma-tab-content">
              <SkillChips skills={groupOf(activeTab)} testid={activeTab} />
            </div>
          </div>
        </div>
      )}
      <style>{styles}</style>
    </div>
  );
}

function SubScore({ label, value, icon }) {
  const color = value >= 80 ? "#10B981" : value >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="ma-subscore">
      <div className="ma-subscore-hdr">{icon}<span>{label}</span></div>
      <div className="ma-subscore-bar"><div className="ma-subscore-fill" style={{ width: `${value}%`, background: color }} /></div>
      <span className="ma-subscore-val" style={{ color }}>{value}%</span>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="ma-stat-pill" style={{ borderColor: `${color}30`, background: `${color}08` }}>
      <span className="ma-stat-dot" style={{ background: color }} />
      <span className="ma-stat-label">{label}</span>
      <span className="ma-stat-val" style={{ color }}>{value}</span>
    </div>
  );
}

function SkillChips({ skills, testid }) {
  const matched = skills.filter(s => s.state === "matched");
  const partial = skills.filter(s => s.state === "partial");
  const missing = skills.filter(s => s.state === "missing");
  return (
    <div className="ma-chips-panel" data-testid={`skill-panel-${testid}`}>
      {matched.length > 0 && (
        <div className="ma-chip-group">
          <h4 className="ma-chip-group-title ma-green-label">Matched ({matched.length})</h4>
          <div className="ma-chips-wrap">
            {matched.map((s, i) => <span key={i} className="ma-chip ma-chip-g" data-testid={`matched-skill-${i}`}>{s.skill}{s.importance === "required" && <em className="ma-req">req</em>}</span>)}
          </div>
        </div>
      )}
      {partial.length > 0 && (
        <div className="ma-chip-group">
          <h4 className="ma-chip-group-title ma-amber-label">Partial ({partial.length})</h4>
          <div className="ma-chips-wrap">
            {partial.map((s, i) => <span key={i} className="ma-chip ma-chip-y" data-testid={`partial-skill-${i}`}>{s.skill}{s.importance === "required" && <em className="ma-req">req</em>}</span>)}
          </div>
        </div>
      )}
      {missing.length > 0 && (
        <div className="ma-chip-group">
          <h4 className="ma-chip-group-title ma-red-label">Missing ({missing.length})</h4>
          <div className="ma-chips-wrap">
            {missing.map((s, i) => <span key={i} className="ma-chip ma-chip-r" data-testid={`missing-skill-${i}`}>{s.skill}{s.importance === "required" && <em className="ma-req">req</em>}</span>)}
          </div>
        </div>
      )}
      {skills.length === 0 && <p className="ma-empty">No skills identified in this category.</p>}
    </div>
  );
}

const styles = `
  .ma-page { max-width: 820px; margin: 0 auto; padding: 28px 16px 60px; }
  .ma-hdr { display: flex; align-items: center; gap: 14px; margin-bottom: 28px; color: var(--text-primary); }
  .ma-h1 { font-size: 1.5rem; font-weight: 700; margin: 0; letter-spacing: -0.02em; }
  .ma-sub { font-size: 0.84rem; color: var(--text-secondary); margin: 2px 0 0; }
  .ma-card { background: var(--bg-surface); border: 1px solid var(--border-main); border-radius: 12px; padding: 24px; margin-bottom: 20px; box-shadow: var(--card-shadow); }
  .ma-field { margin-bottom: 16px; }
  .ma-lbl { display: block; font-size: 0.82rem; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
  .ma-sel-wrap { position: relative; }
  .ma-sel { width: 100%; padding: 10px 36px 10px 12px; border: 1px solid var(--border-main); border-radius: 8px; background: var(--bg-deep); color: var(--text-primary); font-size: 0.88rem; appearance: none; cursor: pointer; }
  .ma-sel:focus { outline: none; border-color: var(--primary); }
  .ma-sel-icon { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .ma-ta { width: 100%; padding: 10px 12px; border: 1px solid var(--border-main); border-radius: 8px; background: var(--bg-deep); color: var(--text-primary); font-size: 0.88rem; resize: vertical; font-family: inherit; box-sizing: border-box; }
  .ma-ta:focus { outline: none; border-color: var(--primary); }
  .ma-ta::placeholder { color: var(--text-muted); }
  .ma-err { color: var(--error); font-size: 0.82rem; margin: 8px 0; }
  .ma-btn-primary { display: flex; align-items: center; gap: 8px; padding: 11px 28px; background: var(--primary); color: #fff; border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: opacity 0.15s, transform 0.1s; }
  .ma-btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .ma-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .ma-credit-note { font-size: 0.76rem; color: var(--text-muted); }
  .ma-spin { animation: ma-sp 1s linear infinite; }
  @keyframes ma-sp { to { transform: rotate(360deg); } }

  .ma-hero-card { padding: 28px; }
  .ma-hero { display: flex; align-items: center; gap: 32px; flex-wrap: wrap; }
  .ma-ring-wrap { position: relative; width: 140px; height: 140px; flex-shrink: 0; }
  .ma-ring-svg { width: 100%; height: 100%; }
  .ma-ring-anim { transition: stroke-dasharray 1s ease; }
  .ma-ring-center { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
  .ma-ring-num { font-size: 2.6rem; font-weight: 800; line-height: 1; }
  .ma-ring-pct { font-size: 1rem; font-weight: 600; color: var(--text-muted); margin-left: 2px; margin-top: 6px; }
  .ma-hero-info { flex: 1; min-width: 260px; display: flex; flex-direction: column; gap: 16px; }
  .ma-rec-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 20px; font-size: 0.82rem; font-weight: 600; width: fit-content; }
  .ma-rec-g { background: rgba(16,185,129,0.1); color: #059669; }
  .ma-rec-y { background: rgba(245,158,11,0.1); color: #D97706; }
  .ma-rec-r { background: rgba(239,68,68,0.1); color: #DC2626; }
  .ma-sub-scores { display: flex; gap: 16px; flex-wrap: wrap; }
  .ma-subscore { flex: 1; min-width: 140px; }
  .ma-subscore-hdr { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; }
  .ma-subscore-bar { height: 6px; border-radius: 3px; background: var(--border-main); overflow: hidden; }
  .ma-subscore-fill { height: 100%; border-radius: 3px; transition: width 0.8s ease; }
  .ma-subscore-val { font-size: 0.88rem; font-weight: 700; margin-top: 4px; display: block; }
  .ma-stats-row { display: flex; gap: 10px; flex-wrap: wrap; }
  .ma-stat-pill { display: flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 8px; border: 1px solid; font-size: 0.78rem; }
  .ma-stat-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .ma-stat-label { color: var(--text-secondary); font-weight: 500; }
  .ma-stat-val { font-weight: 700; }

  .ma-imp-title { display: flex; align-items: center; gap: 8px; font-size: 0.95rem; font-weight: 700; margin: 0 0 14px; color: var(--text-primary); }
  .ma-imp-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
  .ma-imp-item { font-size: 0.84rem; padding: 9px 14px; border-radius: 8px; border: 1px solid; line-height: 1.45; }
  .ma-imp-r { background: rgba(239,68,68,0.05); border-color: rgba(239,68,68,0.15); color: var(--text-primary); }
  .ma-imp-y { background: rgba(245,158,11,0.05); border-color: rgba(245,158,11,0.18); color: var(--text-primary); }
  .ma-imp-b { background: rgba(99,102,241,0.05); border-color: rgba(99,102,241,0.15); color: var(--text-primary); }
  .ma-imp-hint { font-size: 0.76rem; color: var(--text-muted); margin: 12px 0 0; }

  .ma-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--border-main); margin: -24px -24px 20px; padding: 0 24px; }
  .ma-tab { display: flex; align-items: center; gap: 6px; padding: 12px 16px; font-size: 0.82rem; font-weight: 600; color: var(--text-secondary); border: none; background: none; cursor: pointer; border-bottom: 2px solid transparent; transition: color 0.15s, border-color 0.15s; }
  .ma-tab:hover { color: var(--text-primary); }
  .ma-tab-active { color: var(--primary); border-bottom-color: var(--primary); }
  .ma-tab-content { min-height: 80px; }

  .ma-chip-group { margin-bottom: 20px; }
  .ma-chip-group:last-child { margin-bottom: 0; }
  .ma-chip-group-title { font-size: 0.78rem; font-weight: 700; margin: 0 0 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  .ma-green-label { color: #059669; }
  .ma-amber-label { color: #D97706; }
  .ma-red-label { color: #DC2626; }
  .ma-chips-wrap { display: flex; flex-wrap: wrap; gap: 8px; }
  .ma-chip { font-size: 0.82rem; font-weight: 600; padding: 5px 14px; border-radius: 8px; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; }
  .ma-chip-g { background: rgba(16,185,129,0.1); color: #059669; border: 1px solid rgba(16,185,129,0.2); }
  .ma-chip-y { background: rgba(245,158,11,0.08); color: #D97706; border: 1px solid rgba(245,158,11,0.18); }
  .ma-chip-r { background: rgba(239,68,68,0.08); color: #DC2626; border: 1px solid rgba(239,68,68,0.15); }
  .ma-req { font-style: normal; font-size: 0.6rem; font-weight: 800; text-transform: uppercase; opacity: 0.7; letter-spacing: 0.05em; }
  .ma-empty { font-size: 0.84rem; color: var(--text-muted); padding: 20px; text-align: center; }

  @media (max-width: 600px) {
    .ma-hero { flex-direction: column; align-items: center; text-align: center; }
    .ma-hero-info { align-items: center; }
    .ma-stats-row { justify-content: center; }
    .ma-sub-scores { justify-content: center; }
    .ma-tabs { overflow-x: auto; }
  }
`;
